import {
    AlertTriangle,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Clock,
    CornerUpLeft,
    Download01,
    RefreshCw02,
    XCircle,
} from '@untitledui/icons'
import clsx from 'clsx'
import { formatEuro } from 'components/billing/debtorNew/format'
import { Card, StatusPill } from 'components/billing/debtorNew/ui'
import ListItemSelector from 'components/ListItemSelector'
import { format } from 'date-fns'
import { SepaBatch, SepaBatchInvoice, useSepaBatches } from 'hooks/sepaBatchHooks'
import { useState } from 'react'

const FULFILLED = 'fulfilled'

/** Per-row selection is offered for non-fulfilled invoices only. */
interface InvoiceSelection {
    selectedIds: number[]
    selectableIds: number[]
    onToggleOne: (invoiceId: number) => void
    onToggleAll: () => void
}

interface InvoiceRowsProps {
    invoices: SepaBatchInvoice[]
    selection?: InvoiceSelection
}

/** Row-level data as in the invoices overview: nr, provider, date, amount, status. */
const InvoiceRows = ({ invoices, selection }: InvoiceRowsProps) => {
    const allSelected =
        selection !== undefined &&
        selection.selectableIds.length > 0 &&
        selection.selectableIds.every((id) => selection.selectedIds.includes(id))

    return (
        <table className="w-full text-md">
            <thead>
                <tr className="border-b border-neutral-100 text-left text-sm uppercase tracking-wide text-neutral-400">
                    {selection && (
                        <th
                            className="w-10 px-4 py-2"
                            title="Select all invoices that are not fulfilled yet">
                            <ListItemSelector
                                isSelected={allSelected}
                                select={selection.onToggleAll}
                            />
                        </th>
                    )}
                    <th className="px-4 py-2 font-medium">Invoice</th>
                    <th className="px-4 py-2 font-medium">Provider</th>
                    <th className="px-4 py-2 font-medium">Country</th>
                    <th className="px-4 py-2 font-medium">Date</th>
                    <th className="px-4 py-2 text-right font-medium">Payout</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                </tr>
            </thead>
            <tbody>
                {invoices.map((invoice) => {
                    const isSelectable =
                        selection !== undefined &&
                        selection.selectableIds.includes(invoice.invoice_id)
                    const isSelected =
                        selection !== undefined &&
                        selection.selectedIds.includes(invoice.invoice_id)

                    return (
                        <tr
                            key={invoice.invoice_id}
                            onClick={() =>
                                isSelectable && selection.onToggleOne(invoice.invoice_id)
                            }
                            className={clsx(
                                'border-b border-neutral-50 transition-colors',
                                isSelectable && 'cursor-pointer hover:bg-neutral-50',
                                isSelected && 'bg-primary-50 hover:bg-primary-50',
                            )}>
                            {selection && (
                                <td
                                    className="px-4 py-2"
                                    onClick={(event) => event.stopPropagation()}>
                                    <ListItemSelector
                                        isSelected={isSelected}
                                        select={() => selection.onToggleOne(invoice.invoice_id)}
                                        disabled={!isSelectable}
                                    />
                                </td>
                            )}
                            <td className="px-4 py-2 font-medium tabular-nums text-dark">
                                {invoice.invoice_nr}
                            </td>
                            <td className="px-4 py-2 text-neutral-600">
                                {invoice.provider?.business_name ||
                                    `#${invoice.service_provider_id}`}
                            </td>
                            <td className="px-4 py-2 text-neutral-400">
                                {invoice.provider?.country_code}
                            </td>
                            <td className="px-4 py-2 tabular-nums text-neutral-400">
                                {format(new Date(invoice.datetime), 'd MMM yyyy')}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-dark">
                                {formatEuro(Math.abs(invoice.amount_netto))}
                            </td>
                            <td className="px-4 py-2">
                                <StatusPill status={invoice.transaction_status || ''} />
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

type BatchAction = 'fulfil-all' | 'fulfil-selected' | 'release-selected'

interface BatchCardProps {
    batch: SepaBatch
    onDownload: () => void
    onLoadInvoices: (batchId: number) => Promise<SepaBatchInvoice[]>
    onFulfil: (invoiceIds: number[]) => Promise<void>
    onRelease: (batchId: number, invoiceIds: number[]) => Promise<void>
}

const BatchCard = ({ batch, onDownload, onLoadInvoices, onFulfil, onRelease }: BatchCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false)
    // Invoice rows load on demand (expand or batch action); the collapsed
    // card renders from the batch columns alone.
    const [invoices, setInvoices] = useState<SepaBatchInvoice[] | null>(null)
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
    const [pendingAction, setPendingAction] = useState<BatchAction | null>(null)
    const [isBusy, setIsBusy] = useState(false)
    const [selectedIds, setSelectedIds] = useState<number[]>([])

    const allFulfilled = batch.fulfilled_count >= batch.invoice_count
    const fulfilledCount = batch.fulfilled_count
    const loadedInvoices = invoices ?? []
    const openInvoiceIds = loadedInvoices
        .filter((invoice) => invoice.transaction_status !== FULFILLED)
        .map((invoice) => invoice.invoice_id)
    const selectedTotal = loadedInvoices
        .filter((invoice) => selectedIds.includes(invoice.invoice_id))
        .reduce((sum, invoice) => sum + Math.abs(invoice.amount_netto), 0)
    const Chevron = isExpanded ? ChevronDown : ChevronRight

    const loadInvoices = async (): Promise<SepaBatchInvoice[]> => {
        setIsLoadingInvoices(true)
        try {
            const rows = await onLoadInvoices(batch.sepa_batch_id)
            setInvoices(rows)
            return rows
        } finally {
            setIsLoadingInvoices(false)
        }
    }

    const toggleExpanded = () => {
        const expanding = !isExpanded
        setIsExpanded(expanding)
        setSelectedIds([])
        setPendingAction(null)
        if (expanding && invoices === null && !isLoadingInvoices) {
            loadInvoices()
        }
    }

    const startFulfilAll = async () => {
        // The confirm shows the open-invoice count, so the rows must be there.
        if (invoices === null) {
            await loadInvoices()
        }
        setPendingAction('fulfil-all')
    }

    const toggleOne = (invoiceId: number) =>
        setSelectedIds((current) =>
            current.includes(invoiceId)
                ? current.filter((id) => id !== invoiceId)
                : [...current, invoiceId],
        )

    const toggleAll = () =>
        setSelectedIds(openInvoiceIds.every((id) => selectedIds.includes(id)) ? [] : openInvoiceIds)

    const confirmCopy = (() => {
        if (pendingAction === 'fulfil-all') {
            return {
                title: `Set all ${openInvoiceIds.length} open invoices of this batch to fulfilled?`,
                description:
                    'Do this after the bank processed the file. Same action as on the ' +
                    'invoices page (sends the "invoice paid" follow-ups).',
                confirmLabel: 'Yes, set to fulfilled',
            }
        }
        if (pendingAction === 'fulfil-selected') {
            return {
                title: `Set ${selectedIds.length} selected invoices (${formatEuro(selectedTotal)}) to fulfilled?`,
                description:
                    'Do this for the payments the bank actually processed. Same action ' +
                    'as on the invoices page (sends the "invoice paid" follow-ups).',
                confirmLabel: 'Yes, set to fulfilled',
            }
        }
        if (pendingAction === 'release-selected') {
            return {
                title: `Release ${selectedIds.length} selected invoices (${formatEuro(selectedTotal)}) from this batch?`,
                description:
                    'Only do this for payments the bank rejected (bounced IBAN, closed ' +
                    'account). The invoices go back to the creation tab so they can go ' +
                    'into a new file after the bank details are fixed. The stored XML ' +
                    'stays as it was sent to the bank.',
                confirmLabel: 'Yes, release from batch',
            }
        }
        return null
    })()

    const runPendingAction = async () => {
        const action = pendingAction
        setPendingAction(null)
        setIsBusy(true)
        try {
            if (action === 'fulfil-all') {
                await onFulfil(openInvoiceIds)
            } else if (action === 'fulfil-selected') {
                await onFulfil(selectedIds)
            } else if (action === 'release-selected') {
                await onRelease(batch.sepa_batch_id, selectedIds)
            }
            setSelectedIds([])
            if (invoices !== null) {
                await loadInvoices()
            }
        } finally {
            setIsBusy(false)
        }
    }

    return (
        <Card>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <button
                    type="button"
                    onClick={toggleExpanded}
                    className="group flex min-w-0 flex-1 items-center gap-3 text-left">
                    <Chevron className="h-4 w-4 shrink-0 text-neutral-400 transition-colors group-hover:text-neutral-600" />
                    <div className="min-w-0">
                        <p className="truncate text-md font-semibold text-dark">{batch.filename}</p>
                        <p className="flex items-center gap-1 text-sm text-neutral-400">
                            <Clock className="h-3.5 w-3.5" />
                            {format(new Date(batch.created), 'd MMM yyyy HH:mm')}
                            {batch.created_by_user && ` · ${batch.created_by_user.username}`}
                        </p>
                    </div>
                </button>

                <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-sm font-medium text-neutral-600">
                    {batch.country_code}
                </span>
                <span className="text-sm tabular-nums text-neutral-500">
                    {batch.invoice_count} invoices
                </span>
                <span className="text-md font-semibold tabular-nums text-dark">
                    {formatEuro(Number(batch.total_amount))}
                </span>
                {allFulfilled && (
                    <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-sm font-medium text-green-700">
                        <CheckCircle className="h-3 w-3" />
                        fulfilled
                    </span>
                )}
                {!allFulfilled && fulfilledCount > 0 && (
                    <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-sm font-medium tabular-nums text-amber-700">
                        <Clock className="h-3 w-3" />
                        {fulfilledCount}/{batch.invoice_count} fulfilled
                    </span>
                )}

                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onDownload}
                        className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-md font-semibold text-neutral-700 shadow-xs ring-1 ring-neutral-200 transition hover:bg-neutral-50">
                        <Download01 className="h-4 w-4" />
                        Download XML
                    </button>
                    {!allFulfilled && (
                        <button
                            type="button"
                            onClick={startFulfilAll}
                            disabled={isBusy || isLoadingInvoices}
                            className={clsx(
                                'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-md font-semibold transition',
                                isBusy || isLoadingInvoices
                                    ? 'cursor-not-allowed bg-neutral-100 text-neutral-400'
                                    : 'bg-primary text-white hover:opacity-90',
                            )}>
                            <CheckCircle className="h-4 w-4" />
                            {isBusy ? 'Working…' : 'Set all to fulfilled'}
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && selectedIds.length > 0 && (
                <div className="mx-4 mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2">
                    <span className="text-md font-medium tabular-nums text-neutral-600">
                        {selectedIds.length} selected · {formatEuro(selectedTotal)}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPendingAction('fulfil-selected')}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-md font-semibold text-neutral-700 shadow-xs ring-1 ring-neutral-200 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:text-neutral-400">
                            <CheckCircle className="h-4 w-4" />
                            Set to fulfilled
                        </button>
                        <button
                            type="button"
                            onClick={() => setPendingAction('release-selected')}
                            disabled={isBusy}
                            title="For payments the bank rejected: back to the creation tab"
                            className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-md font-semibold text-amber-700 shadow-xs ring-1 ring-amber-200 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:text-neutral-400">
                            <CornerUpLeft className="h-4 w-4" />
                            Release from batch
                        </button>
                    </div>
                </div>
            )}

            {confirmCopy && (
                <div className="mx-4 mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-md font-semibold text-amber-800">{confirmCopy.title}</p>
                    <p className="mt-0.5 text-sm text-amber-700">{confirmCopy.description}</p>
                    <div className="mt-2.5 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={runPendingAction}
                            className="rounded-lg bg-primary px-3 py-2 text-md font-semibold text-white transition hover:opacity-90">
                            {confirmCopy.confirmLabel}
                        </button>
                        <button
                            type="button"
                            onClick={() => setPendingAction(null)}
                            className="rounded-lg bg-white px-3 py-2 text-md font-semibold text-neutral-600 shadow-xs ring-1 ring-neutral-200 transition hover:bg-neutral-50">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {isExpanded && (
                <div className="border-t border-neutral-100">
                    {invoices === null ? (
                        <p className="flex items-center justify-center gap-2 px-4 py-6 text-md text-neutral-400">
                            <RefreshCw02 className="h-4 w-4 animate-spin" />
                            Loading invoices…
                        </p>
                    ) : (
                        <InvoiceRows
                            invoices={loadedInvoices}
                            selection={{
                                selectedIds,
                                selectableIds: openInvoiceIds,
                                onToggleOne: toggleOne,
                                onToggleAll: toggleAll,
                            }}
                        />
                    )}
                </div>
            )}
        </Card>
    )
}

const SepaOverviewTab = () => {
    const {
        batches,
        legacyInvoices,
        isFetching,
        error,
        fetchBatchInvoices,
        downloadBatch,
        setInvoicesFulfilled,
        releaseInvoices,
    } = useSepaBatches()
    const [showLegacy, setShowLegacy] = useState(false)
    const LegacyChevron = showLegacy ? ChevronDown : ChevronRight

    return (
        <div className="flex flex-col gap-3">
            {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-md text-red-700">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {error}
                </div>
            )}

            {batches.length === 0 && isFetching && (
                <Card className="flex items-center justify-center gap-2 px-4 py-10 text-md text-neutral-400">
                    <RefreshCw02 className="h-4 w-4 animate-spin" />
                    Loading SEPA batches…
                </Card>
            )}

            {batches.length === 0 && !isFetching && (
                <Card className="px-4 py-10 text-center text-md text-neutral-400">
                    No SEPA batches yet. Create one on the &ldquo;SEPA creation&rdquo; tab.
                </Card>
            )}

            {batches.map((batch) => (
                <BatchCard
                    key={batch.sepa_batch_id}
                    batch={batch}
                    onDownload={() => downloadBatch(batch.sepa_batch_id)}
                    onLoadInvoices={fetchBatchInvoices}
                    onFulfil={setInvoicesFulfilled}
                    onRelease={releaseInvoices}
                />
            ))}

            {legacyInvoices.count > 0 && (
                <Card>
                    <button
                        type="button"
                        onClick={() => setShowLegacy(!showLegacy)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left">
                        <LegacyChevron className="h-4 w-4 shrink-0 text-neutral-400" />
                        <div>
                            <p className="flex items-center gap-1.5 text-md font-semibold text-dark">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                Before batch tracking
                            </p>
                            <p className="text-sm text-neutral-400">
                                {legacyInvoices.count} sepa_created invoices from before this page
                                existed — their file was downloaded once and was not stored, so
                                there is nothing to re-download.
                            </p>
                        </div>
                    </button>
                    {showLegacy && (
                        <div className="border-t border-neutral-100">
                            <InvoiceRows invoices={legacyInvoices.invoices} />
                        </div>
                    )}
                </Card>
            )}
        </div>
    )
}

export default SepaOverviewTab
